import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, MapPin, Warehouse, Truck, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  useGetCourierCollectionFees,
  useUpdateCourierCollectionFees,
  getGetCourierCollectionFeesQueryKey,
  type CourierCollectionFeeConfig,
  type CourierCollectionFeeSettings,
  type CourierCollectionZoneFee,
} from "@workspace/api-client-react";

type Zone = "A" | "B" | "C" | "D";
const ZONES: Zone[] = ["A", "B", "C", "D"];

type DraftZone = { zone: Zone; firstParcel: string; subsequentParcel: string };
type DraftMapping = { locality: string; postcode: string };
type ZoneKey = "zones" | "deliveryZones";
export type Draft = {
  zones: DraftZone[];
  deliveryZones: DraftZone[];
  localityPostcodes: DraftMapping[];
  deliveryFee: string;
  insuranceFee: string;
  congestionFee: string;
};

const fmt = (n: number) => n.toFixed(2);
const zoneDraft = (list: CourierCollectionZoneFee[] | undefined): DraftZone[] =>
  [...(list ?? [])]
    .sort((a, b) => a.zone.localeCompare(b.zone))
    .map((z) => ({ zone: z.zone as Zone, firstParcel: fmt(z.firstParcel), subsequentParcel: fmt(z.subsequentParcel) }));

export function toDraft(c: CourierCollectionFeeConfig): Draft {
  return {
    zones: zoneDraft(c.zones),
    deliveryZones: zoneDraft(c.deliveryZones),
    localityPostcodes: (c.localityPostcodes ?? []).map((m) => ({ locality: m.locality, postcode: m.postcode })),
    deliveryFee: fmt(c.deliveryFee),
    insuranceFee: fmt(c.insuranceFee),
    congestionFee: fmt(c.congestionFee),
  };
}

/** Parse a money string to a non-negative value with at most 2dp; null if invalid. */
export function parseMoney(v: string): number | null {
  const t = v.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 && n <= 10000 ? n : null;
}

/** Normalise a full UK postcode (outward + inward); null if it is not full-postcode shaped. */
export function normalisePostcode(v: string): string | null {
  const t = v.trim().toUpperCase().replace(/\s+/g, "");
  const m = /^([A-Z]{1,2}\d[A-Z\d]?)(\d[A-Z]{2})$/.exec(t);
  return m ? `${m[1]} ${m[2]}` : null;
}

export function validateDraft(d: Draft): { config: CourierCollectionFeeConfig | null; errors: string[] } {
  const errors: string[] = [];
  const zoneList = (list: DraftZone[], label: string) => {
    const seen = new Set<string>();
    return list.map((z) => {
      if (seen.has(z.zone)) errors.push(`${label} zone ${z.zone} is listed twice.`);
      seen.add(z.zone);
      const f = parseMoney(z.firstParcel);
      const s = parseMoney(z.subsequentParcel);
      if (f == null) errors.push(`${label} zone ${z.zone} first parcel must be a non-negative amount up to £10,000 (pence precision).`);
      if (s == null) errors.push(`${label} zone ${z.zone} subsequent parcel must be a non-negative amount up to £10,000 (pence precision).`);
      return { zone: z.zone, firstParcel: f ?? 0, subsequentParcel: s ?? 0 };
    });
  };
  const zones = zoneList(d.zones, "Collection");
  const deliveryZones = zoneList(d.deliveryZones, "Delivery");
  const extra = (label: string, v: string) => {
    const n = parseMoney(v);
    if (n == null) errors.push(`${label} must be a non-negative amount up to £10,000 (pence precision).`);
    return n ?? 0;
  };
  const seenLoc = new Set<string>();
  const localityPostcodes = d.localityPostcodes.map((m, i) => {
    const locality = m.locality.trim();
    const row = `Mapping ${i + 1}`;
    if (!locality) errors.push(`${row}: servicing locality is required.`);
    else if (locality.length > 120) errors.push(`${row}: servicing locality must be 120 characters or fewer.`);
    const key = locality.toLowerCase();
    if (locality && seenLoc.has(key)) errors.push(`${row}: locality "${locality}" is already mapped (names are not case-sensitive).`);
    seenLoc.add(key);
    const postcode = normalisePostcode(m.postcode);
    if (!postcode) errors.push(`${row}: "${m.postcode.trim() || "blank"}" is not a full UK postcode (for example CV31 1XT).`);
    return { locality, postcode: postcode ?? m.postcode.trim() };
  });
  const config: CourierCollectionFeeConfig = {
    schemaVersion: 2,
    zones,
    deliveryZones,
    localityPostcodes,
    deliveryFee: extra("Depot delivery fee", d.deliveryFee),
    insuranceFee: extra("Insurance fee", d.insuranceFee),
    congestionFee: extra("Congestion charge", d.congestionFee),
  };
  return { config: errors.length ? null : config, errors };
}

const inputCls = "border border-slate-300 rounded px-2 py-1 w-24 text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-duffells-grey/30 disabled:opacity-60";

function ZoneTable({
  title, subtitle, icon, tableKey, label, rows, editing, saving, onChange,
}: {
  title: string; subtitle: string; icon: React.ReactNode; tableKey: ZoneKey; label: string;
  rows: DraftZone[]; editing: boolean; saving: boolean; onChange: (rows: DraftZone[]) => void;
}) {
  const used = new Set(rows.map((z) => z.zone));
  const missing = ZONES.filter((z) => !used.has(z));
  const setZone = (i: number, patch: Partial<DraftZone>) => onChange(rows.map((z, j) => (j === i ? { ...z, ...patch } : z)));
  const p = tableKey === "zones" ? "collection" : "delivery";
  return (
    <section aria-label={title} data-testid={`table-${p}-zones`}>
      <div className="px-4 pt-3 pb-2 flex items-start gap-2">
        <span className="mt-0.5 text-duffells-grey/60">{icon}</span>
        <div>
          <h4 className="text-sm font-semibold text-duffells-grey">{title}</h4>
          <p className="text-xs text-duffells-grey/60">{subtitle}</p>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-duffells-grey/60">
          <tr>
            <th className="px-4 py-2 text-left">Zone</th>
            <th className="px-4 py-2 text-right">First parcel</th>
            <th className="px-4 py-2 text-right">Each subsequent</th>
            {editing && <th className="px-4 py-2"><span className="sr-only">Actions</span></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((z, i) => (
            <tr key={z.zone} className="border-t border-slate-100" data-testid={`row-${p}-zone-${z.zone}`}>
              <td className="px-4 py-2 font-medium">Zone {z.zone}</td>
              <td className="px-4 py-2 text-right tabular-nums">
                {editing ? <input inputMode="decimal" aria-label={`${label} zone ${z.zone} first parcel`} disabled={saving} className={inputCls} value={z.firstParcel} onChange={(e) => setZone(i, { firstParcel: e.target.value })} data-testid={`input-${p}-first-${z.zone}`} /> : `£${z.firstParcel}`}
              </td>
              <td className="px-4 py-2 text-right tabular-nums">
                {editing ? <input inputMode="decimal" aria-label={`${label} zone ${z.zone} subsequent parcel`} disabled={saving} className={inputCls} value={z.subsequentParcel} onChange={(e) => setZone(i, { subsequentParcel: e.target.value })} data-testid={`input-${p}-subsequent-${z.zone}`} /> : `£${z.subsequentParcel}`}
              </td>
              {editing && (
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="ghost" disabled={saving} aria-label={`Remove ${label.toLowerCase()} zone ${z.zone}`} onClick={() => onChange(rows.filter((_, j) => j !== i))} data-testid={`button-remove-${p}-zone-${z.zone}`}><Trash2 className="w-3 h-3" /></Button>
                </td>
              )}
            </tr>
          ))}
          {missing.map((z) => (
            <tr key={`missing-${z}`} className="border-t border-slate-100 bg-amber-50/50" data-testid={`row-${p}-zone-missing-${z}`}>
              <td className="px-4 py-2 font-medium text-duffells-grey/70">Zone {z}</td>
              <td colSpan={2} className="px-4 py-2 text-right text-xs text-amber-800">No fee set: lines in this zone stay as queries until a fee is entered.</td>
              {editing && (
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="ghost" disabled={saving} aria-label={`Add ${label.toLowerCase()} zone ${z}`} onClick={() => onChange([...rows, { zone: z, firstParcel: "", subsequentParcel: "" }].sort((a, b) => a.zone.localeCompare(b.zone)))} data-testid={`button-add-${p}-zone-${z}`}><Plus className="w-3 h-3 mr-1" />Add</Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function LocalityMappings({
  rows, editing, saving, onChange,
}: { rows: DraftMapping[]; editing: boolean; saving: boolean; onChange: (rows: DraftMapping[]) => void }) {
  const setRow = (i: number, patch: Partial<DraftMapping>) => onChange(rows.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  return (
    <section aria-label="Servicing locality postcodes" className="border-t border-slate-100" data-testid="section-locality-postcodes">
      <div className="px-4 pt-3 pb-2 flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 mt-0.5 text-duffells-grey/60" />
          <div>
            <h4 className="text-sm font-semibold text-duffells-grey">Servicing locality to origin postcode</h4>
            <p className="text-xs text-duffells-grey/60 max-w-2xl">
              DHL reports a servicing locality (not the physical locality) for each collection. On invoice Check, a locality with no saved row is looked up automatically by town name (a unique town or city is preferred over suburbs), and the postcode found is saved here. Saved rows and your edits always take precedence over lookups. The postcode stands for the servicing locality, not proof of the actual street address; it sets the collection zone and the congestion match. The destination always comes from the invoice postcode. Unknown or ambiguous localities stay visible queries.
            </p>
          </div>
        </div>
        {editing && (
          <Button size="sm" variant="outline" disabled={saving} onClick={() => onChange([...rows, { locality: "", postcode: "" }])} data-testid="button-add-locality-mapping"><Plus className="w-3 h-3 mr-1" />Add mapping</Button>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-3 text-xs text-duffells-grey/60 bg-slate-50/60 border-t border-slate-100" data-testid="text-no-locality-mappings">
          No saved mappings yet. They are filled in automatically as invoices are checked, and you can add or correct them here.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-duffells-grey/60">
            <tr>
              <th className="px-4 py-2 text-left">Servicing locality</th>
              <th className="px-4 py-2 text-left">Origin postcode</th>
              {editing && <th className="px-4 py-2"><span className="sr-only">Actions</span></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((m, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`row-locality-mapping-${i}`}>
                <td className="px-4 py-2">
                  {editing ? <input aria-label={`Mapping ${i + 1} servicing locality`} disabled={saving} maxLength={120} className="border border-slate-300 rounded px-2 py-1 w-full max-w-xs" value={m.locality} onChange={(e) => setRow(i, { locality: e.target.value })} data-testid={`input-locality-${i}`} /> : <span data-testid={`text-locality-${i}`}>{m.locality}</span>}
                </td>
                <td className="px-4 py-2">
                  {editing ? <input aria-label={`Mapping ${i + 1} origin postcode`} disabled={saving} maxLength={10} className="border border-slate-300 rounded px-2 py-1 w-32 uppercase font-mono" value={m.postcode} onChange={(e) => setRow(i, { postcode: e.target.value })} data-testid={`input-postcode-${i}`} /> : <span className="font-mono" data-testid={`text-postcode-${i}`}>{m.postcode}</span>}
                </td>
                {editing && (
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="ghost" disabled={saving} aria-label={`Remove mapping ${i + 1}`} onClick={() => onChange(rows.filter((_, j) => j !== i))} data-testid={`button-remove-locality-${i}`}><Trash2 className="w-3 h-3" /></Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/** Pure presentational view; used by tests and the connected section. */
export function CollectionFeesView({
  settings, isAdmin, draft, setDraft, onSave, saving, error, onCancel,
}: {
  settings: CourierCollectionFeeSettings;
  isAdmin: boolean;
  draft: Draft | null;
  setDraft: (d: Draft | null) => void;
  onSave: (c: CourierCollectionFeeConfig) => void;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
}) {
  const editing = draft != null;
  const view = draft ?? toDraft(settings.fees);
  const { errors } = editing ? validateDraft(draft) : { errors: [] as string[] };
  const set = (patch: Partial<Draft>) => draft && setDraft({ ...draft, ...patch });
  const depot = settings.destination || "depot02 Leamington";

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="section-collection-fees">
      <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-duffells-grey">Third-party collection fees</h3>
          <p className="text-xs text-duffells-grey/60 mt-0.5">
            Two routes: collections brought back to {depot}, and collections delivered on to another third party. Both share the collection zones, insurance and congestion charge.
          </p>
        </div>
        {isAdmin && !editing && (
          <Button size="sm" variant="outline" onClick={() => setDraft(toDraft(settings.fees))} data-testid="button-edit-collection-fees">Edit fees</Button>
        )}
      </div>

      <div className="grid lg:grid-cols-2 lg:divide-x divide-slate-100">
        <ZoneTable
          title="Collection by origin zone" tableKey="zones" label="Collection"
          subtitle="Used on both routes. Zone comes from the collection origin postcode; first parcel plus each subsequent piece."
          icon={<Warehouse className="w-4 h-4" />} rows={view.zones} editing={editing} saving={saving}
          onChange={(zones) => set({ zones })}
        />
        <ZoneTable
          title="Delivery to another third party" tableKey="deliveryZones" label="Delivery"
          subtitle="Only for third-party to third-party jobs. Zone comes from the invoice destination postcode."
          icon={<Truck className="w-4 h-4" />} rows={view.deliveryZones} editing={editing} saving={saving}
          onChange={(deliveryZones) => set({ deliveryZones })}
        />
      </div>

      <div className="grid sm:grid-cols-3 gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
        {([["deliveryFee", "Depot delivery fee", `Flat, back to ${depot}`], ["insuranceFee", "Insurance fee", "Once per job, both routes"], ["congestionFee", "Congestion charge", "Once if either end qualifies"]] as const).map(([k, label, hint]) => (
          <div key={k} className="flex items-center justify-between gap-2">
            <label htmlFor={`cf-${k}`} className="text-xs text-duffells-grey/70">
              {label}
              <span className="block text-[11px] text-duffells-grey/50">{hint}</span>
            </label>
            {editing ? <input id={`cf-${k}`} inputMode="decimal" disabled={saving} className={inputCls} value={view[k]} onChange={(e) => set({ [k]: e.target.value } as Partial<Draft>)} data-testid={`input-${k}`} /> : <span className="text-sm text-duffells-grey tabular-nums" data-testid={`text-${k}`}>£{view[k]}</span>}
          </div>
        ))}
      </div>

      <LocalityMappings rows={view.localityPostcodes} editing={editing} saving={saving} onChange={(localityPostcodes) => set({ localityPostcodes })} />

      <ul className="px-4 py-3 border-t border-slate-100 text-xs text-duffells-grey/70 space-y-1 list-disc list-inside">
        <li>Back to depot: collection zone fee by piece count + depot delivery fee + insurance + congestion if the origin qualifies.</li>
        <li>Third party to third party: collection zone fee + delivery zone fee, each by piece count, + insurance + congestion once if either end qualifies.</li>
        <li>Congestion charge applies only to exact full-postcode matches in the supplied list ({settings.congestionPostcodes} postcodes). Zones are matched by postcode prefix ({settings.zonePrefixes} prefixes); the most specific prefix wins.</li>
        <li>A location surcharge flag of Yes (for example Isle of Wight) has no supplied fee and stays in manual review. It is separate from the congestion charge.</li>
        <li>A long-length mismatch always stays a query, even when the collection fee matches. Long-length charges are checked independently.</li>
        <li>Automatic locality lookup uses postcodes.io, with <a href="https://photon.komoot.io" target="_blank" rel="noreferrer" className="underline">Photon</a> where place data is unavailable. Fallback place data: © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer" className="underline">OpenStreetMap contributors</a>.</li>
        <li className="flex-inline"><Info className="inline w-3 h-3 mr-1" />Saved changes apply the next time an invoice is checked. Existing flags on already-checked invoices do not update automatically.</li>
      </ul>

      {editing && (
        <div className="px-4 py-3 border-t border-slate-100 space-y-2">
          {errors.length > 0 && <ul role="alert" className="text-xs text-red-700 space-y-0.5" data-testid="text-collection-fee-errors">{errors.map((e) => <li key={e}>{e}</li>)}</ul>}
          {error && <p role="alert" className="text-xs text-red-700" data-testid="text-collection-fee-save-error">Save failed: {error}</p>}
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving} data-testid="button-cancel-collection-fees">Cancel</Button>
            <Button size="sm" disabled={saving || errors.length > 0} onClick={() => { const r = validateDraft(draft); if (r.config) onSave(r.config); }} data-testid="button-save-collection-fees">
              {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}Save fees
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Connected section. Render with key={courierId} so drafts reset per courier. */
export function CollectionFeesSection({ courierId, isAdmin }: { courierId: number; isAdmin: boolean }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const idRef = useRef(courierId);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { idRef.current = courierId; setDraft(null); setError(null); }, [courierId]);

  const q = useGetCourierCollectionFees(courierId, {
    query: { queryKey: getGetCourierCollectionFeesQueryKey(courierId), enabled: !!courierId, staleTime: 60000 },
  });
  const save = useUpdateCourierCollectionFees({
    mutation: {
      onSuccess: (data, vars) => {
        qc.setQueryData(getGetCourierCollectionFeesQueryKey(vars.id), data);
        qc.invalidateQueries({ queryKey: ["courier-invoices"] });
        if (!mounted.current || vars.id !== idRef.current) return;
        setDraft(null);
        setError(null);
        toast({ title: "Collection fees saved", description: "They apply on the next invoice check." });
      },
      onError: (err: any, vars) => {
        if (mounted.current && vars.id === idRef.current) setError(err?.data?.error || err?.message || "Unknown error");
      },
    },
  });

  if (q.isLoading) return <div className="h-40 rounded-xl border border-slate-200 bg-slate-50 animate-pulse" data-testid="skeleton-collection-fees" />;
  if (q.isError) return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 text-sm flex items-center justify-between">
      <span className="text-red-700">Could not load collection fees.</span>
      <Button size="sm" variant="outline" onClick={() => q.refetch()} data-testid="button-retry-collection-fees">Retry</Button>
    </div>
  );
  if (!q.data?.supported) return null;

  return (
    <CollectionFeesView
      settings={q.data}
      isAdmin={isAdmin}
      draft={draft}
      setDraft={(d) => { setDraft(d); setError(null); }}
      saving={save.isPending}
      error={error}
      onCancel={() => { setDraft(null); setError(null); }}
      onSave={(fees) => save.mutate({ id: courierId, data: fees })}
    />
  );
}
